import {
  Modal,
  ModalActions,
  ModalContent,
  ModalActionButton,
  ModalTitle,
  ModalCloseIconButton,
  Typography,
  Link,
  Button,
  Form,
  TextField,
  usePolicyNumberValidator,
  ButtonGroup,
  NotificationCard,
} from '@ace-clublabs/shared-component-library';
import React, { useEffect, useState } from 'react';
import toolTipImage from '../assets/policy-number-location.png';
import { createAccountOnClick, getPlaceholder } from '../utils/methods';
import { aceClubs, autoPayPath, basePath } from '../shared/constants';
import parse from 'html-react-parser';
import { ClubHelper } from '@ace-clublabs/payments-helper';
import { useGlobalState } from '../shared/state';

interface Props {
  clubCode: string;
  onSubmit: (value: any) => void;
  isMobileApp: boolean;
  fieldError?: string;
  notificationError?: string;
  supportPolicyStar?: boolean;
}

const SubmitInsuranceForm: React.FC<Props> = ({
  onSubmit,
  clubCode,
  fieldError,
  notificationError,
  isMobileApp,
  supportPolicyStar = false,
}) => {
  const [, setPolicyNumber] = useGlobalState('policyNumber');
  const [hideCustomError, setHideCustomError] = useState(false);

  const handleLoginClick = (event: any) => {
    event.preventDefault();
    window.open('https://account.app.ace.aaa.com');
  };

  let characters: string = '12';
  let minLength: Number = 12;
  let regexPattern: string = '^[a-zA-Z]{3}\\d{9}$';

  if (supportPolicyStar) {
    switch (clubCode) {
      case '001':
        characters = '9';
        minLength = 9;
        regexPattern = '^[a-zA-Z]{1}\\d{8}$';
        break;
      case '065':
        characters = '9 or 12';
        minLength = 9;
        regexPattern = '^[a-zA-Z]{3}\\d{9}|^[a-zA-Z]{1}\\d{8}$';
        break;
      default:
        characters = '12';
        minLength = 12;
        regexPattern = '^[a-zA-Z]{3}\\d{9}$';
    }
  }

  const handleLifeLinkClick = (event: any) => {
    event.preventDefault();
    window.open('https://www.aaalife.com/eServices/eservices/account/login.jsp');
  };

  const handleCreateAccountLinkClick = (event: any) => {
    event.preventDefault();
    createAccountOnClick();
  };

  const [toolTipModal, setToolTipModal] = useState(false);
  const closeToolTipModal = () => {
    setToolTipModal(false);
  };
  const toolTipClickHandler = (e: any) => {
    e.preventDefault();
    setToolTipModal(true);
  };
  const isAceClub = aceClubs.find((ac: string) => ac === clubCode);

  const renderFieldError = () => {
    const phoneNumber = ClubHelper.getClubPhoneNumber(clubCode);
    const phoneNumberHref = `<a id='field-error-tel-link' href=tel:${phoneNumber}>${phoneNumber}</a>`;
    const fielderrorJsx = fieldError?.replace('{phoneNumber}', phoneNumberHref);
    return parse(`<span id="field-error">${fielderrorJsx}</span>`);
  };

  useEffect(() => {
    let zipRouterScript: any;
    const { pathname } = window?.location;

    if (window?.location?.hostname?.endsWith('.aaa.com') && [basePath, autoPayPath].includes(pathname)) {
      zipRouterScript = document.createElement('script');
      zipRouterScript.src = 'https://app.ace.aaa.com/ziprouter/1.0.8/ace-zip-router.js';
      zipRouterScript.type = 'text/javascript';
      zipRouterScript.id = 'zip-router-modal-script';
      document.body.appendChild(zipRouterScript);
    }

    return () => {
      if (zipRouterScript) {
        document.body.removeChild(zipRouterScript);
      }
    };
  }, []);

  const handlePolicyInputBlur = () => setHideCustomError(true);
  const displayCustomError = fieldError && !hideCustomError;

  const placeholderPolicyNumber = getPlaceholder(clubCode);
  const helperText = `${characters}-character policy number, e.g. ${placeholderPolicyNumber}`;
  const phoneNumber = ClubHelper.getClubPhoneNumber(clubCode);

  let notificationCardError = <></>;
  switch (notificationError) {
    case 'You are already enrolled in Auto Pay.':
      notificationCardError = (
        <NotificationCard severity="success">
          <Typography variant="b2Regular">
            You are already enrolled in Auto Pay.{' '}
            <Link id="login-link" component="a" href="https://app.ace.aaa.com/payments/manage-autopay/">
              Log in
            </Link>{' '}
            to manage your Auto Pay details.
          </Typography>
        </NotificationCard>
      );
      break;
    case 'No last name on file.':
    case 'We are unable to process your request at this time.':
      notificationCardError = (
        <NotificationCard severity="error">
          <Typography variant="b2Regular">
            We are unable to process your request at this time. Please call{' '}
            <a id="field-error-tel-link" href={`tel:${phoneNumber}`}>
              {phoneNumber}
            </a>{' '}
            for assistance.
          </Typography>
        </NotificationCard>
      );
      break;
    case 'Your policy has expired or been canceled.':
      notificationCardError = (
        <NotificationCard severity="error">
          <Typography variant="b2Regular">
            Your policy has expired or been canceled. Please call{' '}
            <a id="field-error-tel-link" href={`tel:${phoneNumber}`}>
              {phoneNumber}
            </a>{' '}
            for assistance.
          </Typography>
        </NotificationCard>
      );
      break;
    case 'There is no balance due at this time.':
      notificationCardError = (
        <NotificationCard severity="success">
          <Typography variant="b2Regular">Your billing statement has already been paid in full.</Typography>
        </NotificationCard>
      );
      break;
  }

  return (
    <div id="submit-insurance-form">
      <Typography component="h1" variant="h5Medium" id="submit-insurance-header" className="inner-container">
        Enter your policy number
      </Typography>
      {notificationCardError}
      <Form onSubmit={onSubmit}>
        <TextField
          id="policy-number"
          name="policy-number"
          label="Policy number"
          maxLength={12}
          onBlur={handlePolicyInputBlur}
          helperText={helperText}
          placeholder={placeholderPolicyNumber}
          onChange={(e: any) => {
            setHideCustomError(true);
            setPolicyNumber(e.target.value);
          }}
          fullWidth
          validator={usePolicyNumberValidator({
            minLength: 12,
            maxLength: 12,
            pattern: '[A-Z]{3}[0-9]{9}',
            fieldMustBeExactLengthValidationMessage: 'Your policy number must have 12 characters.',
            patternFailedValidationMessage: 'The policy number entered is invalid.',
          })}
          validationErrorMessage={displayCustomError ? renderFieldError() : undefined}
          error={displayCustomError ? true : false}
        />

        <Link id="tooltip-text" className="margin8Bottom" onClick={toolTipClickHandler}>
          Where can I find my policy number?
        </Link>
        <ButtonGroup>
          <Button fullWidth className="margin16TopBottom" id="next-button" type="submit" label="Next" />
        </ButtonGroup>
      </Form>
      {isAceClub && !isMobileApp && (
        <div id="more-ways-section">
          <Typography variant="b1Bold" id="more-ways-text" className="margin8Bottom">
            More ways to pay
          </Typography>
          <div id="log-in-message">
            <Link id="login-link" component="a" onClick={handleLoginClick}>
              Log in
            </Link>{' '}
            to schedule payments, enroll in Auto Pay, and save your payment information for future use.
          </div>
          <br />
          <div id="create-account-text">
            Don't have an account?{' '}
            <Link id="create-account-link" component="a" onClick={handleCreateAccountLinkClick}>
              Create one now.
            </Link>
          </div>
          <br />
          <div id="life-policy-text">
            You can pay your AAA Life Policy at{' '}
            <Link id="life-eservices-link" component="a" onClick={handleLifeLinkClick}>
              AAA Life eServices.
            </Link>
          </div>
        </div>
      )}

      <Modal
        id="tooltip-modal"
        variants={{ xs: 'bottom-sheet', sm: 'regular' }}
        open={toolTipModal}
        onClose={closeToolTipModal}
        size="medium"
        aria-labelledby="tooltip-modal-title-id"
        aria-describedby="tooltip-modal-content-id"
      >
        <ModalTitle id="tooltip-modal-title-id" sx={{ marginRight: '2rem' }}>
          Where can I find my policy number?
          <ModalCloseIconButton aria-label="Close" onClick={closeToolTipModal} />
        </ModalTitle>

        <ModalContent id="tooltip-modal-content-id">
          <>
            <Typography>Your policy number can be found on your</Typography>
            <br />
            <Typography>• Insurance ID card </Typography>
            <Typography>• Policy renewal document</Typography>
            <Typography>• Insurance bill</Typography>
            <img src={toolTipImage} alt="tool-tip" id="tool-tip-image" />
          </>
        </ModalContent>
        <ModalActions
          actionLayout={{
            sm: 'fullWidth',
            md: 'shared',
            lg: 'rightAlign',
          }}
        >
          <ModalActionButton fullWidth label="Got it" onClick={closeToolTipModal} />
        </ModalActions>
      </Modal>
    </div>
  );
};

export default SubmitInsuranceForm;
